"use client"

// Root-layout error boundary (TES-1844).
//
// `app/error.tsx` only wraps the children of the root layout, so an error
// thrown by `app/layout.tsx` itself — or by any module it imports at render
// time — escapes it entirely. Next.js then falls back to its own built-in
// `global-error`, which renders an unstyled light card on a white document and
// reports nothing. That is a P0-shaped visual failure against the dark canvas
// and a blind spot in error reporting, so this file replaces it.
//
// This boundary REPLACES the root layout when it renders, so — per the Next.js
// file convention — it must supply its own `<html>`, `<body>`, global stylesheet
// and font variables. Nothing from `app/layout.tsx` is in scope here. Keep this
// file self-contained: any import that could itself be the thing that threw
// belongs nowhere near the last line of defense.
//
// The Next.js convention also requires this file to be a Client Component, and
// that rules out one thing the layout does freely: calling a `next/font` loader
// (TES-1855 — see the note on CRITICAL_CANVAS_CSS below). The fonts are named
// there instead. Do not reintroduce a loader call here or in anything this
// file imports; it compiles clean and 500s every route in `next dev`.

import React from "react"
import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-react"

import "./globals.css"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DESIGN_THEME_DARK, DESIGN_THEME_LIGHT } from "@/lib/design-theme"
import { THEME_LIGHT_CLASS, resolveActiveTheme } from "@/lib/theme-init-script"

// Mirrors the critical CSS in `app/layout.tsx` so the canvas is already the
// right colour for the reader's theme before the stylesheet chunk lands. A
// white flash here would be the exact failure this boundary exists to prevent.
//
// It also declares the two font variables by hand. This file used to call the
// `next/font/google` loaders itself, but a loader call inside a `"use client"`
// module is rejected by Turbopack's dev-mode font transform — it reported
// "Font loader calls must be assigned to a const" (misleading; they were) and
// 500'd every route in `next dev` while `next build` stayed green (TES-1855).
// `global-error.tsx` has to be a Client Component, and any module it imports is
// pulled into the client graph with it, so there is nowhere on this side of the
// boundary a loader call can legally live.
//
// Naming the family instead of loading it costs nothing when the layout's
// font CSS is already in the document — `next/font` registers a real
// `@font-face` family ('Archivo', from lib/fonts-plate-and-hairline.ts)
// alongside its metric-adjusted 'Archivo Fallback', so these resolve to the
// exact same face the rest of the site uses. Archivo is the ONE family
// (PRODUCT.md: Host Grotesk and Saira are gone from the repo), and both role
// variables resolve to it, as `--tls-font-display` / `--tls-font-body` do in
// globals.css. When the layout never rendered and that CSS is absent, they
// degrade to `system-ui`. Declaring them is what makes that degradation
// graceful: an *undefined* `--font-body` makes every `var(--font-body), …`
// declaration invalid at computed-value time, which drops the page to the
// browser's default serif.
const CRITICAL_CANVAS_CSS = `
  :root {
    --font-body: "Archivo", "Archivo Fallback", system-ui, sans-serif;
    --font-display: var(--font-body);
  }
  body {
    background-color: ${DESIGN_THEME_DARK.canvas};
    color: ${DESIGN_THEME_DARK.textStrong};
    margin: 0;
    padding: 0;
  }
  .light body {
    background-color: ${DESIGN_THEME_LIGHT.canvas};
    color: ${DESIGN_THEME_LIGHT.textStrong};
  }
`

interface GlobalErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  // The root layout's theme bootstrap is a plain inline `<script>`, and that
  // trick does not carry over here: when a *server* render throws, Next serves
  // its own minimal `__next_error__` document and this boundary is mounted on
  // the client, so a `<script>` React creates is never executed. Resolving the
  // preference during render instead puts `.light` on `<html>` in the same
  // commit as everything else — no flash, and no light-theme reader dumped onto
  // the dark canvas because the page they asked for failed.
  const [theme] = React.useState(() =>
    typeof window === "undefined" ? "dark" : resolveActiveTheme()
  )
  const retryRef = React.useRef<HTMLButtonElement>(null)

  // §07.8 "The retry control receives focus when a boundary catches, so
  // keyboard users are not stranded mid-page." Retry is in-place (reset),
  // not location.reload: a translator-induced throw would reload, re-run
  // the translator, and loop if the focused control were a hard reload.
  React.useEffect(() => {
    retryRef.current?.focus()
  }, [])

  React.useEffect(() => {
    // Safety net for the case where Next *does* server-render this boundary:
    // React keeps the server's `<html>` attributes through hydration, which
    // would silently drop the class resolved above (TES-1578).
    const root = document.documentElement
    root.classList.toggle(THEME_LIGHT_CLASS, resolveActiveTheme() === "light")
  }, [])

  React.useEffect(() => {
    // Same reporting path as `app/error.tsx`. `@sentry/browser` is pinned to the
    // identical version as `@sentry/nextjs`, so it reads the client that
    // `instrumentation-client.ts` booted (TES-1841) off the shared global
    // carrier — the capture lands on the same project as every other client
    // event. Dynamic so the SDK is never pulled into a prerender graph.
    import("@sentry/browser")
      .then(({ captureException }) => {
        captureException(error, {
          tags: { context: "RootLayoutErrorBoundary" },
          // A root-layout throw is server-side more often than not, in which
          // case React redacts the message and only the digest survives.
          // Promote it so the event can be joined to the server-side capture.
          extra: { digest: error.digest ?? null },
        })
      })
      .catch(() => {})
  }, [error])

  return (
    <html
      lang="en"
      className={theme === "light" ? THEME_LIGHT_CLASS : undefined}
      suppressHydrationWarning
    >
      <head>
        <meta charSet="utf-8" />
        {/* This boundary bypasses Next's metadata pipeline, so the viewport tag
            has to be written by hand. Without it mobile lays the page out at
            980px and the card renders at a third of its intended size. */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Page error: Tesla Lock Sound</title>
        <style
          // eslint-disable-next-line react-doctor/no-danger
          dangerouslySetInnerHTML={{ __html: CRITICAL_CANVAS_CSS }}
        />
      </head>
      <body className="bg-[var(--tls-canvas)] text-[var(--tls-fg-strong)]">
        <main className="flex min-h-screen items-center justify-center bg-[var(--tls-canvas)] p-4">
          <Card className="w-full max-w-lg border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)]">
            <CardHeader className="text-center">
              <div className="mx-auto mb-6 flex size-20 items-center justify-center rounded-[var(--radius-pill)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)]">
                <AlertTriangle className="size-10 text-[var(--tls-danger)]" />
              </div>
              {/* §07.8: kicker (type-label) names the blast radius; no error
                  code. The digest goes to Sentry (below), never to the reader. */}
              <p className="tls-eyebrow mb-2 [--tls-eyebrow-color:var(--tls-fg-subtle)]">
                Error in the page shell
              </p>
              <CardTitle className="mb-2 font-display text-2xl">
                The page frame failed to load
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2 text-center">
                <p className="text-[var(--tls-fg-strong)]">
                  The shell every page is built on (the navigation, the footer, the
                  audio player) threw before this page could render.
                </p>
                <p className="text-sm text-[var(--tls-fg-muted)]">
                  Reloading fetches a fresh copy of the shell and clears this in most
                  cases. Your saved theme and any download in progress are unaffected.
                </p>
              </div>

              {process.env.NODE_ENV === "development" && (
                <details className="rounded-[var(--radius-3)] border border-[color:color-mix(in_srgb,var(--tls-danger)_32%,transparent)] bg-[color:color-mix(in_srgb,var(--tls-danger)_10%,transparent)] p-4">
                  <summary className="cursor-pointer font-medium text-[var(--tls-danger)] hover:text-[color:color-mix(in_srgb,var(--tls-danger)_72%,var(--tls-fg-strong))]">
                    Developer Debug Info
                  </summary>
                  <div className="mt-3 space-y-2">
                    <div>
                      <span className="font-mono text-xs text-[color:color-mix(in_srgb,var(--tls-danger)_72%,var(--tls-fg-strong))]">
                        Message:
                      </span>
                      <p className="mt-1 rounded-[var(--radius-1)] bg-[color:color-mix(in_srgb,var(--tls-canvas)_36%,transparent)] p-2 font-mono text-sm text-[color:color-mix(in_srgb,var(--tls-danger)_54%,var(--tls-fg-strong))]">
                        {error.message}
                      </p>
                    </div>
                    {error.stack && (
                      <div>
                        <span className="font-mono text-xs text-[color:color-mix(in_srgb,var(--tls-danger)_72%,var(--tls-fg-strong))]">
                          Stack Trace:
                        </span>
                        <pre className="mt-1 max-h-40 overflow-auto rounded-[var(--radius-1)] bg-[color:color-mix(in_srgb,var(--tls-canvas)_36%,transparent)] p-2 font-mono text-xs text-[color:color-mix(in_srgb,var(--tls-danger)_54%,var(--tls-fg-strong))]">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  ref={retryRef}
                  onClick={reset}
                  className="font-display"
                >
                  <RotateCcw className="mr-2 size-4" />
                  Retry without reloading
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="font-display"
                >
                  <RefreshCw className="mr-2 size-4" />
                  Reload the page
                </Button>
              </div>

              <div className="text-center">
                <p className="text-xs text-[var(--tls-fg-subtle)]">
                  Still stuck after a reload? Go straight to{" "}
                  {/* Deliberately a plain <a>, not next/link: a client-side
                      navigation would re-enter the same root layout that just
                      threw. Only a fresh document request escapes it. */}
                  {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                  <a
                    href="/sounds"
                    className="underline underline-offset-4 hover:text-[var(--tls-fg-muted)]"
                  >
                    the sound library
                  </a>
                  , which renders from a different route than the one that failed.
                </p>
              </div>
            </CardContent>
          </Card>
        </main>
      </body>
    </html>
  )
}
